import { memo, useEffect, useState } from 'react'
import { Close, Down, Up, Plus } from '@icon-park/react'

import { Alert, Api, Confirmation } from '@/modules'
import { Button, Divider, Flex, Input } from '@/components'
import { ContentFilter } from '@/components/derived'
import { useRouting } from '@/hooks'
import { useObserver } from '@/modules/observer/observer.context'

import {
  AccordionBody,
  AccordionContainer,
  AccordionHeader,
  AvatarCircle,
  GroupPill,
  LinkText,
  NetBadge,
} from '../../PublisherPersons.styles'
import { PublisherPersonResponse } from '@shared/models'
import { useCompanyContext } from '../../../CompanyDetails.context'
import { GroupTagInput } from '../groupInput/PublisherPersonsGroupInput.component'

import {
  AccordionCount,
  AddGroupRow,
  AddGroupTagWrapper,
  AddProfileBtn,
  AddProfileRow,
  GroupPillRemoveBtn,
  GroupsRow,
  GroupTagsRow,
  ModeSelector,
  ModeSelectorBtn,
  PersonName,
  PersonRow,
  ProfileLinkText,
  ProfileRow,
  ProfilesRow,
} from './PublisherPersonsTable.style'
import { PlatformSelector } from '../plataformSelector/PublisherPersonsPlatformSelector.component'

export const PUBLISHER_PERSONS_TABLE = 'publisher-persons-table'

type GroupByMode = 'grupo' | 'pessoa' | 'rede'

const GROUP_COLORS: Record<string, string> = {
  Políticos: 'blue',
  'Mídia Nacional': 'green',
  'Mídia Regional': 'amber',
  Influenciadores: 'pink',
}

const PLATFORM_ICONS: Record<string, string> = {
  Twitter: '𝕏',
  Instagram: 'IG',
  Facebook: 'FB',
  YouTube: 'YT',
}

function getGroupColor(name: string): string {
  return GROUP_COLORS[name] ?? 'gray'
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

export const PublisherPersonsTable = memo(Component)

function Component(): JSX.Element | null {
  const { companyID } = useRouting()
  const { observe, removeObserver } = useObserver()
  const { sendAlert } = Alert.Hook()

  const [groupBy, setGroupBy] = useState<GroupByMode>('pessoa')
  const [search, setSearch] = useState('')
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set())

  const { isLoadingPublisherPersons, publisherPersons, reloadPublisherPersons } = useCompanyContext()

  const { run: removePerson } = Api.Command<void>(
    Api.Methods.DELETE,
    `companies/${companyID}/publisher-persons/:personID`,
  )

  const { run: removeProfile } = Api.Command<void>(
    Api.Methods.DELETE,
    `companies/${companyID}/publisher-persons/:personID/profiles/:profileID`,
  )

  const { open: openConfirmation } = Confirmation.Hook({
    text: 'Tem certeza que deseja remover?',
    confirmText: 'Sim, remover',
  })

  const [addingGroupTo, setAddingGroupTo] = useState<string | null>(null)
  const [availableGroups, setAvailableGroups] = useState<{ id: string; name: string; isGlobal: boolean }[]>([])

  const { run: fetchGroups } = Api.Command<{ id: string; name: string; isGlobal: boolean }[]>(
    Api.Methods.GET,
    `companies/:companyID/publisher-persons/groups`,
  )

  const { run: assignGroup } = Api.Command<void>(
    Api.Methods.POST,
    `companies/:companyID/publisher-persons/:personID/groups`,
  )

  const { run: createGroup } = Api.Command<{ id: string; name: string }>(
    Api.Methods.POST,
    `companies/:companyID/publisher-persons/groups`,
  )

  const { run: removeGroup } = Api.Command<void>(
    Api.Methods.DELETE,
    'companies/:companyID/publisher-persons/:personID/groups/:groupID',
  )

  const [addingProfileTo, setAddingProfileTo] = useState<string | null>(null)
  const [newProfilePlatform, setNewProfilePlatform] = useState('Twitter')
  const [newProfileUrl, setNewProfileUrl] = useState('')

  const { run: addProfile } = Api.Command<void>(
    Api.Methods.POST,
    'companies/:companyID/publisher-persons/:personID/profiles',
  )

  useEffect(() => {
    reload()
    const id = observe(PUBLISHER_PERSONS_TABLE, reload)
    return () => removeObserver(PUBLISHER_PERSONS_TABLE, id)
  }, [])

  function reload(): void {
    if (!companyID) return
    reloadPublisherPersons({ page: 0, size: 1000 })
  }

  function toggleAccordion(key: string): void {
    setOpenAccordions((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  function handleRemovePerson(personID: string, name: string): void {
    openConfirmation(() => {
      removePerson({
        binds: [['personID', personID]],
        onSuccess: () => {
          sendAlert({ type: 'success', title: 'Removido', message: `${name} removido com sucesso` })
          reload()
        },
      })
    })
  }

  function handleRemoveProfile(personID: string, profileID: string): void {
    openConfirmation(() => {
      removeProfile({
        binds: [
          ['personID', personID],
          ['profileID', profileID],
        ],
        onSuccess: () => {
          sendAlert({ type: 'success', title: 'Perfil removido', message: 'Perfil removido com sucesso' })
          reload()
        },
      })
    })
  }

  function handleRemoveGroup(personID: string, groupID: string): void {
    openConfirmation(() => {
      removeGroup({
        binds: [
          ['companyID', companyID!],
          ['personID', personID],
          ['groupID', groupID],
        ],
        onSuccess: () => {
          sendAlert({ type: 'success', title: 'Removido', message: 'Grupo removido com sucesso' })
          reload()
        },
      })
    })
  }

  function handleOpenAddGroup(person: PublisherPersonResponse): void {
    fetchGroups({
      binds: [['companyID', companyID!]],
      onSuccess: (data) => {
        const alreadyHas = new Set(person.groups.map((g) => g.id))
        setAvailableGroups((data ?? []).filter((g) => !alreadyHas.has(g.id)))
        setAddingGroupTo(person.id)
      },
    })
  }

  async function handleCreateGroup(name: string): Promise<{ id: string; name: string; isGlobal: boolean } | null> {
    return new Promise((resolve) => {
      createGroup({
        input: { name },
        binds: [['companyID', companyID!]],
        onSuccess: (data) => {
          if (!data) return resolve(null)
          resolve({ id: data.id, name: data.name, isGlobal: false })
        },
      })
    })
  }

  function handleAssignGroups(personID: string, groups: { id: string; name: string; isGlobal: boolean }[]): void {
    if (!groups.length) return
    assignGroup({
      input: { groupIDs: groups.map((g) => g.id) },
      binds: [
        ['companyID', companyID!],
        ['personID', personID],
      ],
      onSuccess: () => {
        setAddingGroupTo(null)
        setAvailableGroups([])
        reload()
      },
    })
  }

  function normalizeUrl(url: string): string {
    const trimmed = url.trim()
    if (!trimmed) return trimmed
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  // o campo aceita vários links separados por ';': normaliza um a um
  function normalizeUrlList(value: string): string {
    return value
      .split(';')
      .map((url) => normalizeUrl(url))
      .filter(Boolean)
      .join(';')
  }

  function handleAddProfile(personID: string): void {
    if (!newProfileUrl.trim()) return
    addProfile({
      input: { platform: newProfilePlatform, profileUrl: normalizeUrlList(newProfileUrl) },
      binds: [
        ['companyID', companyID!],
        ['personID', personID],
      ],
      onSuccess: () => {
        setAddingProfileTo(null)
        setNewProfilePlatform('Twitter')
        setNewProfileUrl('')
        reload()
      },
    })
  }

  const filtered = (publisherPersons?.elements ?? []).filter(
    (p) =>
      !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.profiles.some((pr) => pr.profileUrl.toLowerCase().includes(search.toLowerCase())),
  )

  function renderByGroup(): JSX.Element {
    const allGroups = [
      ...new Map((publisherPersons?.elements ?? []).flatMap((p) => p.groups).map((g) => [g.id, g])).values(),
    ]

    return (
      <>
        {allGroups.map((group) => {
          const items = filtered.filter((p) => p.groups.some((g) => g.id === group.id))
          if (!items.length) return null
          const isOpen = openAccordions.has(group.id)
          return (
            <AccordionContainer key={group.id}>
              <AccordionHeader onClick={() => toggleAccordion(group.id)}>
                {isOpen ?
                  <Up size={14} />
                : <Down size={14} />}
                <GroupPill $color={getGroupColor(group.name)}>{group.name}</GroupPill>
                <AccordionCount>{items.length} pessoas</AccordionCount>
              </AccordionHeader>
              {isOpen && (
                <AccordionBody>
                  {items.map((person) => (
                    <PersonRow key={person.id}>
                      <AvatarCircle>{getInitials(person.name)}</AvatarCircle>
                      <Flex $type="column" $align="stretch" $gap={4} style={{ flex: 1 }}>
                        <PersonName>{person.name}</PersonName>
                        <ProfilesRow>
                          {person.profiles.map((pr) => (
                            <NetBadge key={pr.id} $platform={pr.platform}>
                              {PLATFORM_ICONS[pr.platform] ?? pr.platform}{' '}
                              <LinkText>{pr.profileUrl.replace('https://', '')}</LinkText>
                            </NetBadge>
                          ))}
                        </ProfilesRow>
                      </Flex>
                      <Button.Root $variant="link" onClick={() => handleRemovePerson(person.id, person.name)}>
                        <Button.Icon value={Close} />
                      </Button.Root>
                    </PersonRow>
                  ))}
                </AccordionBody>
              )}
            </AccordionContainer>
          )
        })}
      </>
    )
  }

  function renderByPerson(): JSX.Element {
    return (
      <>
        {filtered.map((person) => {
          const isOpen = openAccordions.has(person.id)
          const isAddingGroup = addingGroupTo === person.id
          return (
            <AccordionContainer key={person.id}>
              <AccordionHeader onClick={() => toggleAccordion(person.id)}>
                {isOpen ?
                  <Up size={14} />
                : <Down size={14} />}
                <AvatarCircle>{getInitials(person.name)}</AvatarCircle>
                <PersonName>{person.name}</PersonName>
                <GroupTagsRow>
                  {person.groups.map((g) => (
                    <GroupPill key={g.id} $color={getGroupColor(g.name)}>
                      {g.name}
                      <GroupPillRemoveBtn
                        onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                          e.stopPropagation()
                          handleRemoveGroup(person.id, g.id)
                        }}>
                        ×
                      </GroupPillRemoveBtn>
                    </GroupPill>
                  ))}
                </GroupTagsRow>
                <AccordionCount>{person.profiles.length} perfis</AccordionCount>
                <Button.Root
                  $variant="link"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation()
                    handleRemovePerson(person.id, person.name)
                  }}>
                  <Button.Icon value={Close} />
                </Button.Root>
              </AccordionHeader>
              {isOpen && (
                <AccordionBody>
                  {person.profiles.map((pr) => (
                    <ProfileRow key={pr.id}>
                      <NetBadge $platform={pr.platform}>{PLATFORM_ICONS[pr.platform] ?? pr.platform}</NetBadge>
                      <ProfileLinkText>{pr.profileUrl.replace('https://', '')}</ProfileLinkText>
                      <Button.Root $variant="link" onClick={() => handleRemoveProfile(person.id, pr.id)}>
                        <Button.Icon value={Close} />
                      </Button.Root>
                    </ProfileRow>
                  ))}

                  {addingProfileTo === person.id ?
                    <AddProfileRow>
                      <PlatformSelector value={newProfilePlatform} onChange={setNewProfilePlatform} />
                      <div style={{ flex: 1 }}>
                        <Input
                          placeholder={`https://${newProfilePlatform.toLowerCase()}.com/perfil (vários links: separe por ;)`}
                          value={newProfileUrl}
                          onChange={setNewProfileUrl}
                        />
                      </div>
                      <Button.Root $variant="primary" onClick={() => handleAddProfile(person.id)}>
                        <Button.Icon value={Plus} />
                      </Button.Root>
                      <Button.Root
                        $variant="link"
                        onClick={() => {
                          setAddingProfileTo(null)
                          setNewProfilePlatform('Twitter')
                          setNewProfileUrl('')
                        }}>
                        <Button.Icon value={Close} />
                      </Button.Root>
                    </AddProfileRow>
                  : <AddProfileBtn>
                      <Button.Root
                        $variant="link"
                        onClick={(e) => {
                          e.stopPropagation()
                          setAddingProfileTo(person.id)
                        }}>
                        <Button.Icon value={Plus} />
                        <Button.Text value="Adicionar rede" />
                      </Button.Root>
                    </AddProfileBtn>
                  }

                  <AddGroupRow>
                    {isAddingGroup ?
                      <>
                        <AddGroupTagWrapper>
                          <GroupTagInput
                            groups={availableGroups}
                            selected={[]}
                            onChange={(groups) => handleAssignGroups(person.id, groups)}
                            onCreate={handleCreateGroup}
                          />
                        </AddGroupTagWrapper>
                        <Button.Root
                          $variant="link"
                          onClick={() => {
                            setAddingGroupTo(null)
                            setAvailableGroups([])
                          }}>
                          <Button.Icon value={Close} />
                        </Button.Root>
                      </>
                    : <Button.Root
                        $variant="link"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenAddGroup(person)
                        }}>
                        <Button.Icon value={Plus} />
                        <Button.Text value="Adicionar grupo" />
                      </Button.Root>
                    }
                  </AddGroupRow>
                </AccordionBody>
              )}
            </AccordionContainer>
          )
        })}
      </>
    )
  }

  function renderByNetwork(): JSX.Element {
    const platforms = [
      ...new Set((publisherPersons?.elements ?? []).flatMap((p) => p.profiles.map((pr) => pr.platform))),
    ]
    return (
      <>
        {platforms.map((platform) => {
          const items = filtered.filter((p) => p.profiles.some((pr) => pr.platform === platform))
          if (!items.length) return null
          const isOpen = openAccordions.has(platform)
          return (
            <AccordionContainer key={platform}>
              <AccordionHeader onClick={() => toggleAccordion(platform)}>
                {isOpen ?
                  <Up size={14} />
                : <Down size={14} />}
                <NetBadge $platform={platform}>
                  {PLATFORM_ICONS[platform] ?? platform} {platform}
                </NetBadge>
                <AccordionCount>{items.length} pessoas</AccordionCount>
              </AccordionHeader>
              {isOpen && (
                <AccordionBody>
                  {items.map((person) => {
                    const prs = person.profiles.filter((pr) => pr.platform === platform)
                    return prs.map((pr) => (
                      <PersonRow key={pr.id}>
                        <AvatarCircle>{getInitials(person.name)}</AvatarCircle>
                        <Flex $type="column" $align="stretch" $gap={2} style={{ flex: 1 }}>
                          <PersonName>{person.name}</PersonName>
                          <LinkText>{pr.profileUrl.replace('https://', '')}</LinkText>
                        </Flex>
                        <GroupsRow>
                          {person.groups.map((g) => (
                            <GroupPill key={g.id} $color={getGroupColor(g.name)}>
                              {g.name}
                            </GroupPill>
                          ))}
                        </GroupsRow>
                        <Button.Root $variant="link" onClick={() => handleRemoveProfile(person.id, pr.id)}>
                          <Button.Icon value={Close} />
                        </Button.Root>
                      </PersonRow>
                    ))
                  })}
                </AccordionBody>
              )}
            </AccordionContainer>
          )
        })}
      </>
    )
  }

  return (
    <>
      <Divider.Root>
        <Divider.Title value="Pessoas cadastradas" />
        <Divider.Spacer />
        <Divider.Subtitle
          value={`${publisherPersons?.elements?.length ?? 0} de ${publisherPersons?.total ?? 0} pessoa${publisherPersons?.total !== 1 ? 's' : ''}`}
        />
      </Divider.Root>

      <Flex $type="row" $align="center" $gap={12} style={{ marginBottom: 16 }}>
        <ContentFilter
          isLoading={isLoadingPublisherPersons}
          onApply={(val) => setSearch(val ?? '')}
          onReset={() => setSearch('')}
          placeholder="Buscar por nome ou link..."
        />
        <ModeSelector>
          {(['pessoa', 'grupo', 'rede'] as GroupByMode[]).map((mode) => (
            <ModeSelectorBtn key={mode} $active={groupBy === mode} onClick={() => setGroupBy(mode)}>
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </ModeSelectorBtn>
          ))}
        </ModeSelector>
      </Flex>

      {groupBy === 'grupo' && renderByGroup()}
      {groupBy === 'pessoa' && renderByPerson()}
      {groupBy === 'rede' && renderByNetwork()}
    </>
  )
}
